import { api } from '@/lib/api';
import type {
  CreatePresignedUploadRequest,
  CreatePresignedUploadResponse,
  DceDocumentType,
  DceSearchRequest,
  DceSearchResultChunk,
  DceUpload,
  DceUploadDocument,
} from '../types/dce-upload.types';

const TM_BASE = '/tm';

export interface UploadProgressEvent {
  /** Index dans le tableau `files` du presign. */
  fileIndex: number;
  /** 0..1, ou null si la taille du fichier n'est pas connue. */
  ratio: number | null;
  /** Octets uploadés cumulés pour ce fichier. */
  loaded: number;
  /** Octets totaux du fichier (si connu). */
  total: number | null;
}

export const dceUploadService = {
  /** Étape 1 : crée le tracker + récupère N URLs présignées PUT. */
  async createPresignedUpload(
    opportunityId: number,
    payload: CreatePresignedUploadRequest,
  ): Promise<CreatePresignedUploadResponse> {
    const { data } = await api.post<CreatePresignedUploadResponse>(
      `${TM_BASE}/opportunities/${opportunityId}/dce/presign`,
      payload,
    );
    return data;
  },

  /** Étape 2 : confirme la fin des uploads → déclenche le pipeline BullMQ. */
  async confirmUpload(
    opportunityId: number,
    uploadId: number,
    uploadedPaths: string[],
  ): Promise<DceUpload> {
    const { data } = await api.post<DceUpload>(
      `${TM_BASE}/opportunities/${opportunityId}/dce/confirm/${uploadId}`,
      { uploadedPaths },
    );
    return data;
  },

  /** Liste les uploads DCE d'une opportunité. */
  async listByOpportunity(opportunityId: number): Promise<DceUpload[]> {
    const { data } = await api.get<DceUpload[]>(
      `${TM_BASE}/opportunities/${opportunityId}/dce/uploads`,
    );
    return data;
  },

  /** Détail d'un upload (utilisé par le polling de progression). */
  async getById(uploadId: number): Promise<DceUpload> {
    const { data } = await api.get<DceUpload>(`${TM_BASE}/dce/uploads/${uploadId}`);
    return data;
  },

  /** Liste les OpportunityDocument issus d'un upload (chargé à l'expansion). */
  async listDocuments(uploadId: number): Promise<DceUploadDocument[]> {
    const { data } = await api.get<DceUploadDocument[]>(
      `${TM_BASE}/dce/uploads/${uploadId}/documents`,
    );
    return data;
  },

  /** Reclassement manuel d'un document (UI de revue Sprint 2). */
  async reclassifyDocument(
    uploadId: number,
    documentId: number,
    dceDocumentType: DceDocumentType,
    lotId?: number | null,
  ): Promise<void> {
    await api.patch(
      `${TM_BASE}/dce/uploads/${uploadId}/documents/${documentId}/classification`,
      { dceDocumentType, ...(lotId !== undefined ? { lotId } : {}) },
    );
  },

  /** Validation finale de la classification d'un batch. */
  async approveClassification(uploadId: number): Promise<DceUpload> {
    const { data } = await api.post<DceUpload>(
      `${TM_BASE}/dce/uploads/${uploadId}/approve`,
    );
    return data;
  },

  /** Soft delete d'un upload DCE. */
  async softDelete(uploadId: number): Promise<void> {
    await api.delete(`${TM_BASE}/dce/uploads/${uploadId}`);
  },

  /** Recherche RAG dans les DCE indexés d'une opportunité. */
  async searchDce(
    opportunityId: number,
    request: DceSearchRequest,
  ): Promise<DceSearchResultChunk[]> {
    const { data } = await api.post<DceSearchResultChunk[]>(
      `${TM_BASE}/opportunities/${opportunityId}/dce/search`,
      request,
    );
    return data;
  },

  /**
   * Upload direct navigateur → MinIO via URL présignée PUT, avec event progress.
   * Utilise XHR plutôt que fetch pour récupérer XMLHttpRequestUpload.onprogress.
   */
  async uploadFileToPresignedUrl(
    file: File,
    presignedUrl: string,
    onProgress?: (event: UploadProgressEvent) => void,
    fileIndex = 0,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      if (file.type) {
        xhr.setRequestHeader('Content-Type', file.type);
      }
      xhr.upload.onprogress = (evt) => {
        if (!onProgress) return;
        onProgress({
          fileIndex,
          ratio: evt.lengthComputable ? evt.loaded / evt.total : null,
          loaded: evt.loaded,
          total: evt.lengthComputable ? evt.total : null,
        });
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) {
            onProgress({
              fileIndex,
              ratio: 1,
              loaded: file.size,
              total: file.size,
            });
          }
          resolve();
        } else {
          reject(new Error(`Upload MinIO échoué (HTTP ${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error('Erreur réseau lors de l\'upload MinIO'));
      xhr.send(file);
    });
  },
};
